import { getSessionId, identify, Identify, track } from '@amplitude/unified';
import { LogsEvent } from '@datadog/browser-logs';
import moment from 'moment-timezone';

import env from 'config/env';

export const USE_LOGGING = process.env.NODE_ENV === 'development';

type TrackArgs = Parameters<typeof track>;
type TrackReturn = ReturnType<typeof track>;

type logEventType = (...args: TrackArgs) => TrackReturn | undefined;

/**
 * Real user id for Efekta Insights, set by `useInsightsIdentity` (see
 * `analytics/index.tsx`) whenever the app's own user context resolves.
 * Deliberately NOT sourced from Amplitude's `getUserId()` (EFEKTA-18944):
 * `dev-tf` runs with no Amplitude API key, so Amplitude never initializes
 * there and `getUserId()` always returns undefined even though the real user
 * is known the whole time.
 */
let insightsUserId: string | null = null;

export const setInsightsUserId = (userId: string | null): void => {
  insightsUserId = userId;
};

/**
 * Fire-and-forget dual-write to Efekta Insights alongside the existing
 * Amplitude call (EFEKTA-18848). `env.INSIGHTS` is the feature flag — only
 * `dev-tf` sets it today (see config/envByHost/dev-tf.ts), so this is a no-op
 * everywhere else, prod included, until a follow-up ticket turns it on there.
 * Never awaited and never throws: a failure here must not affect the
 * Amplitude call, its return value, or the teacher-facing UX.
 */
const sendToInsights = (eventInput: TrackArgs[0], eventProperties: TrackArgs[1]): void => {
  const insights = env.INSIGHTS;
  if (!insights) {
    return;
  }

  try {
    const isEventObject = typeof eventInput !== 'string';
    const sessionId = getSessionId();

    const body = JSON.stringify({
      event: isEventObject ? eventInput.event_type : eventInput,
      properties: (isEventObject ? eventInput.event_properties : eventProperties) ?? {},
      userId: insightsUserId,
      sessionId: sessionId !== undefined ? String(sessionId) : null,
      clientTs: new Date().toISOString(),
    });

    fetch(insights.INGEST_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-insights-write-key': insights.WRITE_KEY },
      body,
      keepalive: true,
    }).catch(() => undefined); // swallow network/HTTP failures — never surfaced to the caller
  } catch {
    // swallow synchronous failures too (e.g. JSON.stringify on a circular value)
  }
};

export const logEvent: logEventType = (...args) => {
  if (USE_LOGGING) {
    console.log(`${args[0]}: ${JSON.stringify(args[1])}`); // eslint-disable-line no-console
  } else {
    sendToInsights(args[0], args[1]);
    return track(...args);
  }
};

type setUserPropertiesReturn = ReturnType<typeof identify>;

type setUserPropertiesType = (segmentation: Record<string, unknown>) => setUserPropertiesReturn | undefined;

export const setUserProperties: setUserPropertiesType = (segmentation: Record<string, unknown>) => {
  if (USE_LOGGING) {
    console.log(`set user properties: ${JSON.stringify(segmentation)}`); // eslint-disable-line no-console
  } else {
    const identifySet = new Identify();

    type ValidIdentityType = Parameters<(typeof identifySet)['set']>[1];

    Object.entries(segmentation).forEach(([key, value]) => {
      identifySet.set(key, value as ValidIdentityType);
    });
    return identify(identifySet);
  }
};

export const weeksProcessor = (weeks: string[]) => {
  const currentWeekStart = moment().startOf('isoWeek');
  return weeks.map((weekString) => moment(weekString, 'YYYY-MM-DDTHH:mm:ss.SSSSZ').diff(currentWeekStart, 'weeks'));
};

export const isBefore24H = (isoDateString: string) =>
  moment(isoDateString, 'YYYY-MM-DDTHH:mm:ss.SSSSZ').diff(moment(), 'h') < 24;

export const amplitudePostFailed = (log: LogsEvent) =>
  log.http?.url?.includes('amplitude.com') || log?.message?.includes('Amplitude Logger [Error]');

export const getRawIndexFailure = (log: LogsEvent) =>
  log?.message?.includes("Cannot read properties of undefined (reading 'getRawIndex')");

const ignoreList = [amplitudePostFailed, getRawIndexFailure];

export const discardNoiseFromLogs = (log: LogsEvent) => {
  if (ignoreList.some((fn) => fn(log))) {
    return false;
  }

  return true;
};
